import React, { useState, useEffect } from 'react';
import { 
  LayoutDashboard, Users, Warehouse, Plane, Fuel, FileText, Settings, 
  Menu, X, LogOut, Bell, User, Building2, Calendar, MessageSquare, Car,
  Globe, ClipboardList, TrendingUp, Wrench, Package, Key, Cpu,
  Truck, Receipt, DollarSign, Activity
} from 'lucide-react';

// Import des composants
import DashboardView from './components/DashboardView';
import ClientsView from './components/ClientsView';
import HangarsView from './components/HangarsView';
import AircraftView from './components/AircraftView';
import FuelView from './components/FuelView';
import InvoicesView from './components/InvoicesView';
import SettingsView from './components/SettingsView';
import ScheduleView from './components/ScheduleView';
import ChatView from './components/ChatView';
import 'leaflet/dist/leaflet.css';
import VehiclesView from './components/VehiclesView';
import SuperAdminDashboard from './pages/superadmin/SuperAdminDashboard';
import PackagesManager from './pages/superadmin/PackagesManager';
import LicensesManager from './pages/superadmin/LicensesManager';
import AgentsManager from './pages/superadmin/AgentsManager';
import DetectionsViewer from './pages/superadmin/DetectionsViewer';
import ClientRequestsView from './components/staff/ClientRequestsView';
import FuelAnalytics from './components/analytics/FuelAnalytics';
import MaintenanceAnalytics from './components/analytics/MaintenanceAnalytics';
import FinancialAnalytics from './components/analytics/FinancialAnalytics';
import SuppliersView from './components/Suppliers/SuppliersView';
import SupplierInvoicesView from './components/SupplierInvoices/SupplierInvoicesView';
import TakeoffLandingAnalytics from './components/analytics/TakeoffLandingAnalytics';
import HangarLeaseAnalytics from './components/analytics/hangar-lease/HangarLeaseAnalytics';
import { Camera } from 'lucide-react';

// Import Auth
import { useAuth } from './contexts/AuthContext';

// ── Niveaux autorisés pour l'onglet Flight Analytics ──────────────────────────
const FLIGHT_ANALYTICS_LEVELS = new Set([90, 100, 110]);

// ── Niveaux autorisés pour l'onglet Settings ──────────────────────────────────
const SETTINGS_LEVELS = new Set([90, 100]);

const App = () => {
  const { user, logout, isAuthenticated, loading, hasMinLevel } = useAuth();
  const [activeTab, setActiveTab] = useState('dashboard');
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [notifications] = useState(3);

  // Vérifier si l'utilisateur est Super Admin (level 100)
  const isSuperAdmin = user?.permissions?.level >= 100 || user?.role === 'administrator' || user?.role === 'super_admin';

  // Helper pour lire le level depuis localStorage ou l'objet user
  const getUserLevel = () => {
    const storedLevel = localStorage.getItem('user_permission_level');
    return storedLevel ? parseInt(storedLevel) : (user?.permissions?.level ?? 0);
  };

  // Vérifier si l'utilisateur peut accéder aux Flight Analytics (niveaux 90, 100, 110)
  const canViewFlightAnalytics = FLIGHT_ANALYTICS_LEVELS.has(getUserLevel());

  // Vérifier si l'utilisateur peut accéder aux Settings (niveaux 90 et 100 uniquement)
  const canViewSettings = SETTINGS_LEVELS.has(getUserLevel());

  // Écouter les événements de navigation personnalisés
  useEffect(() => {
    const handleNavigate = (event) => {
      const { tab } = event.detail;
      if (tab) {
        setActiveTab(tab);
      }
    };
    
    window.addEventListener('navigate', handleNavigate);
    
    return () => {
      window.removeEventListener('navigate', handleNavigate);
    };
  }, []);

  // TOUS les menus (sans flightAnalytics ni settings — ajoutés conditionnellement plus bas)
  const menuItems = [
    { id: 'dashboard',        label: 'Dashboard',         icon: LayoutDashboard, color: 'text-blue-400'    },
    { id: 'schedule',         label: 'Schedule',          icon: Calendar,        color: 'text-cyan-400'    },
    { id: 'chat',             label: 'Chat',              icon: MessageSquare,   color: 'text-pink-400'    },
    { id: 'clientRequests',   label: 'Client Requests',   icon: ClipboardList,   color: 'text-purple-400'  },
    { id: 'clients',          label: 'Clients',           icon: Users,           color: 'text-green-400'   },
    { id: 'suppliers',        label: 'Suppliers',         icon: Truck,           color: 'text-emerald-400' },
    { id: 'hangars',          label: 'Hangars & Leases',  icon: Warehouse,       color: 'text-yellow-400'  },
    { id: 'aircraft',         label: 'Aircraft',          icon: Plane,           color: 'text-purple-400'  },
    { id: 'fuel',             label: 'Fuel Management',   icon: Fuel,            color: 'text-orange-400'  },
    { id: 'invoices',         label: 'Invoices',          icon: FileText,        color: 'text-red-400'     },
    { id: 'supplierInvoices', label: 'Supplier Invoices', icon: Receipt,         color: 'text-indigo-400'  },
    { id: 'vehicles',         label: 'Vehicles',          icon: Car,             color: 'text-sky-400'     },
    { id: 'fuelAnalytics',    label: 'Fuel Analytics',    icon: TrendingUp,      color: 'text-indigo-400'  },
    { id: 'maintAnalytics',   label: 'Maint Analytics',   icon: Wrench,          color: 'text-red-400'     },
    { id: 'financialAnalytics', label: 'Financial Analytics', icon: DollarSign,  color: 'text-green-400'   },
    { id: 'hangarLeaseAnalytics', label: 'Hangar & Lease Analytics', icon: Warehouse, color: 'text-yellow-400' },
  ];

  // ── Ajouter Flight Analytics seulement pour les niveaux 90 / 100 / 110 ──────
  if (canViewFlightAnalytics) {
    menuItems.push({
      id: 'flightAnalytics',
      label: 'Flight Analytics',
      icon: Activity,
      color: 'text-sky-400',
    });
  }

  // ── Ajouter Settings seulement pour les niveaux 90 et 100 ────────────────────
  if (canViewSettings) {
    menuItems.push({ id: 'settings', label: 'Settings', icon: Settings, color: 'text-gray-400' });
  }

  // Ajouter Super Admin si l'utilisateur a les droits
  if (isSuperAdmin) {
    menuItems.push(
      { id: 'superadmin', label: 'Airports',  icon: Building2, color: 'text-red-400'    },
      { id: 'detections',     label: 'Detections', icon: Camera,    color: 'text-cyan-400'    },
      { id: 'packages',   label: 'Packages',  icon: Package,   color: 'text-purple-400' },
      { id: 'licenses',   label: 'Licenses',  icon: Key,       color: 'text-yellow-400' },
      { id: 'agents',     label: 'Agents',    icon: Cpu,       color: 'text-cyan-400'   }
    );
  }

  const renderContent = () => {
    // Guard: si l'onglet flightAnalytics est demandé mais l'utilisateur n'a pas les droits
    if (activeTab === 'flightAnalytics' && !canViewFlightAnalytics) {
      return (
        <div className="flex flex-col items-center justify-center h-64 text-center">
          <div className="p-4 bg-red-500/10 border border-red-500/30 rounded-xl mb-4">
            <Activity size={32} className="text-red-400 mx-auto mb-2" />
            <p className="text-red-400 font-semibold">Access Restricted</p>
            <p className="text-gray-400 text-sm mt-1">
              Flight Analytics requires a permission level of 90, 100, or 110.
            </p>
          </div>
        </div>
      );
    }

    // Guard: si l'onglet settings est demandé mais l'utilisateur n'a pas les droits
    if (activeTab === 'settings' && !canViewSettings) {
      return (
        <div className="flex flex-col items-center justify-center h-64 text-center">
          <div className="p-4 bg-red-500/10 border border-red-500/30 rounded-xl mb-4">
            <Settings size={32} className="text-red-400 mx-auto mb-2" />
            <p className="text-red-400 font-semibold">Access Restricted</p>
            <p className="text-gray-400 text-sm mt-1">
              Settings requires a permission level of 90 or 100.
            </p>
          </div>
        </div>
      );
    }

    switch(activeTab) {
      case 'dashboard':          return <DashboardView />;
      case 'schedule':           return <ScheduleView />;
      case 'chat':               return <ChatView />;
      case 'clientRequests':     return <ClientRequestsView />;
      case 'clients':            return <ClientsView />;
      case 'suppliers':          return <SuppliersView />;
      case 'hangars':            return <HangarsView />;
      case 'aircraft':           return <AircraftView />;
      case 'fuel':               return <FuelView />;
      case 'invoices':           return <InvoicesView />;
      case 'supplierInvoices':   return <SupplierInvoicesView />;
      case 'vehicles':           return <VehiclesView />;
      case 'fuelAnalytics':      return <FuelAnalytics />;
      case 'maintAnalytics':     return <MaintenanceAnalytics />;
      case 'financialAnalytics': return <FinancialAnalytics />;
      case 'flightAnalytics':    return <TakeoffLandingAnalytics />;
      case 'hangarLeaseAnalytics': return <HangarLeaseAnalytics />;
      case 'settings':           return <SettingsView />;
      case 'superadmin':         return <SuperAdminDashboard />;
      case 'detections':         return <DetectionsViewer />;
      case 'packages':           return <PackagesManager />;
      case 'licenses':           return <LicensesManager />;
      case 'agents':             return <AgentsManager />;
      default:                   return <DashboardView />;
    }
  };

  const handleLogout = () => {
    logout();
  };

  // Afficher l'écran de chargement
  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-yellow-500 mx-auto mb-4"></div>
          <p className="text-gray-400">Loading...</p>
        </div>
      </div>
    );
  }

  // Si non authentifié, on retourne null (le ProtectedRoute gère la redirection)
  if (!isAuthenticated) {
    return null;
  }

  // Nom d'affichage de l'utilisateur
  const displayName = user ? `${user.first_name} ${user.last_name}` : 'Admin';
  const displayRole = user ? user.role?.charAt(0).toUpperCase() + user.role?.slice(1) : 'Administrator';

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900">
      {/* Sidebar */}
      <div className={`fixed left-0 top-0 h-full bg-gray-900/95 backdrop-blur-sm border-r border-gray-700 transition-all duration-300 z-20 ${sidebarOpen ? 'w-64' : 'w-20'}`}>
        <div className="p-4 border-b border-gray-700 flex items-center justify-between">
          {sidebarOpen && (
            <div>
              <h1 className="text-xl font-bold text-white">aeroTrick</h1>
              <p className="text-xs text-gray-500">FBO Management</p>
            </div>
          )}
          <button onClick={() => setSidebarOpen(!sidebarOpen)} className="text-gray-400 hover:text-white">
            {sidebarOpen ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>
        
        <nav className="p-3 space-y-1 overflow-y-auto h-[calc(100%-140px)]">
          {menuItems.map((item) => (
            <button
              key={item.id}
              onClick={() => setActiveTab(item.id)}
              className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all duration-200 ${
                activeTab === item.id 
                  ? 'bg-blue-600/20 text-blue-400 border-l-2 border-blue-500' 
                  : 'text-gray-400 hover:bg-gray-800/50 hover:text-white'
              }`}
            >
              <item.icon size={20} className={activeTab === item.id ? item.color : ''} />
              {sidebarOpen && <span className="text-sm">{item.label}</span>}
            </button>
          ))}
        </nav>
        
        <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-gray-700">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-full bg-blue-600 flex items-center justify-center">
              <User size={16} className="text-white" />
            </div>
            {sidebarOpen && (
              <div className="flex-1">
                <p className="text-sm text-white">{displayName}</p>
                <p className="text-xs text-gray-500">{displayRole}</p>
              </div>
            )}
            <button onClick={handleLogout} className="text-gray-400 hover:text-white">
              <LogOut size={18} />
            </button>
          </div>
        </div>
      </div>

      {/* Main content */}
      <div className={`transition-all duration-300 ${sidebarOpen ? 'ml-64' : 'ml-20'}`}>
        {/* Top bar */}
        <div className="bg-gray-900/80 backdrop-blur-sm border-b border-gray-700 px-6 py-3 flex justify-between items-center">
          <h2 className="text-white font-semibold">
            {menuItems.find(m => m.id === activeTab)?.label || 'Dashboard'}
          </h2>
          <div className="flex items-center gap-4">
            <button className="relative text-gray-400 hover:text-white">
              <Bell size={20} />
              {notifications > 0 && (
                <span className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 rounded-full text-xs text-white flex items-center justify-center">
                  {notifications}
                </span>
              )}
            </button>
          </div>
        </div>
        
        {/* Page content */}
        <div className="p-6">
          {renderContent()}
        </div>
      </div>
    </div>
  );
};

export default App;